/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import config from 'js/app/config';
import useRouter from 'js/lib/useRouter';

import { Link, Typography, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import PrivacyNotification from 'bundles/account-profile/components/recruiting-profile-viewer/components/privacy-settings/PrivacyNotification';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import { DeepLinkParams } from 'bundles/account-profile/components/types';
import { NotFound } from 'bundles/common/components/withNotFound';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  container: (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(32)};
  `,
  privacyContainer: (theme: Theme) => css`
    margin-top: ${theme.spacing(32)};
  `,
};

export const NotFoundOfPrivateView = () => {
  const { sharedEventingV3Data } = useContext(ProfileContext);
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_error', {
    errorName: EVENTS.notFoundPrivate,
    ...sharedEventingV3Data,
  });

  return (
    <TrackedDiv
      trackingName={EVENTS.notFoundPrivate}
      withVisibilityTracking
      trackClicks={false}
      requireFullyVisible={false}
    >
      <div ref={visibilityTrackingRef}>
        <div css={styles.privacyContainer}>
          <PrivacyNotification
            inlineActionHref={`${config.url.base}account-profile?deepLink=${DeepLinkParams.PRIVACY_SETTINGS}`}
            linkType="internal"
          />
        </div>
        <NotFound
          description={
            <div css={styles.container}>
              <Typography variant="d2semibold">{_t(`The profile you're looking for can't be found`)}</Typography>
              <Typography2 component="p" variant="bodyPrimary">
                <FormattedMessage
                  message={_t(
                    `To keep looking, check that you've got the correct link, try your search again, or {homePageLink}. But it's also possible that the profile was either removed or set to private.`
                  )}
                  homePageLink={
                    <Link variant="standard" href={config.url.base}>
                      {_t('navigate back to the homepage')}
                    </Link>
                  }
                />
              </Typography2>
              <Typography2 component="p" variant="bodyPrimary">
                <FormattedMessage
                  message={_t('If you continue having trouble, {supportEmail}.')}
                  supportEmail={
                    // eslint-disable-next-line no-restricted-syntax
                    <Link variant="standard" href="https://support.coursera.org">
                      {_t('contact our support team')}
                    </Link>
                  }
                />
              </Typography2>
            </div>
          }
        />
      </div>
    </TrackedDiv>
  );
};

export const NotFoundOfRecruiterView = () => {
  const { sharedEventingV3Data } = useContext(ProfileContext);
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_error', {
    errorName: EVENTS.notFoundRecruiter,
    ...sharedEventingV3Data,
  });

  return (
    <TrackedDiv
      trackClicks={false}
      withVisibilityTracking
      trackingName={EVENTS.notFoundRecruiter}
      requireFullyVisible={false}
    >
      <div ref={visibilityTrackingRef}>
        <NotFound
          description={
            <div css={styles.container}>
              <Typography variant="d2semibold">{_t('This candidate is no longer available')}</Typography>
              <Typography2 component="p" variant="bodyPrimary">
                {_t(
                  'This profile was either removed or set to private and will no longer be available on your dashboard within the next 24 hours. Go back to your dashboard to find more candidates that meet your criteria.'
                )}
              </Typography2>
              <Typography2 component="p" variant="bodyPrimary">
                <FormattedMessage
                  message={_t('If you continue having trouble, {supportEmail}.')}
                  supportEmail={
                    // eslint-disable-next-line no-restricted-syntax
                    <Link variant="standard" href="mailto:talentsupport@coursera.org">
                      {_t('contact our support team')}
                    </Link>
                  }
                />
              </Typography2>
            </div>
          }
        />
      </div>
    </TrackedDiv>
  );
};

export const LearnerProfileNotFoundView = () => {
  const router = useRouter();
  const isFromRecruitingDashboard = router.location?.query?.source === 'recruitingDashboard';

  return isFromRecruitingDashboard ? <NotFoundOfRecruiterView /> : <NotFoundOfPrivateView />;
};
